import { Dismiss20Regular, Save20Regular } from '@fluentui/react-icons';
import { useState, type FormEvent } from 'react';

export interface GuardarComoTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (name: string, description: string | null) => void;
  isSaving: boolean;
}

export function GuardarComoTemplateModal({
  isOpen,
  onClose,
  onConfirm,
  isSaving,
}: GuardarComoTemplateModalProps): JSX.Element | null {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (name.trim()) {
      onConfirm(name.trim(), description.trim() || null);
    }
  };

  const handleClose = (): void => {
    if (!isSaving) {
      setName('');
      setDescription('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="bg-card border border-border shadow-lg w-full max-w-md" style={{ borderRadius: 'var(--radius-card)' }}>
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 className="text-foreground font-semibold" style={{ fontSize: 'var(--text-title3)' }}>
              Guardar como Template
            </h2>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSaving}
              className="text-secondary-text hover:text-foreground transition-colors disabled:opacity-50"
              aria-label="Fechar"
            >
              <Dismiss20Regular className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-4">
            <div>
              <label htmlFor="template-name" className="block text-foreground mb-2" style={{ fontSize: 'var(--text-body)' }}>
                Nome do Template <span className="text-destructive">*</span>
              </label>
              <input
                id="template-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSaving}
                required
                className="w-full px-3 py-2 bg-background border border-input text-foreground rounded focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                style={{ borderRadius: 'var(--radius)', fontSize: 'var(--text-body)' }}
                placeholder="Ex: Moradia T3 - Estrutura Base"
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="template-description" className="block text-foreground mb-2" style={{ fontSize: 'var(--text-body)' }}>
                Descrição (opcional)
              </label>
              <textarea
                id="template-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSaving}
                rows={3}
                className="w-full px-3 py-2 bg-background border border-input text-foreground rounded focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 resize-none"
                style={{ borderRadius: 'var(--radius)', fontSize: 'var(--text-body)' }}
                placeholder="Breve descrição do template..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSaving}
              className="px-4 py-2 bg-secondary hover:bg-accent border border-border text-foreground rounded transition-colors disabled:opacity-50"
              style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-body)' }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving || !name.trim()}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-body)' }}
            >
              <Save20Regular className="w-4 h-4" />
              {isSaving ? 'A guardar...' : 'Guardar Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
